import React, { ChangeEvent, ReactElement, UIEvent, useEffect, useRef, useState } from 'react';

import { ARCHIVED } from '../../../../../utils/constants';
import { formatUsername, getClassNames, retrieveFromLocalStorage } from '../../../../../utils/util';

import ProgrammeLayout from '../../../../../layouts/programme/ProgrammeLayout';

import { SupportMessage } from '../../../../../types/entity.types';

import { ReactComponent as ArrowIcon } from '../../../../../resources/icons/arrow-single.svg';
import { ReactComponent as SearchIcon } from '../../../../../resources/icons/search.svg';
import { ReactComponent as SendIcon } from '../../../../../resources/icons/send.svg';

import './support-chat.scss';

type Props = {
  status: string;
  messageOwner?: string;
  messages: SupportMessage[];
  createMessage: (messageOwner: string, callback: () => void) => void;
  navigateBack: () => void;
};

const SupportChat = ({ status, messageOwner = '', messages, createMessage, navigateBack }: Props): ReactElement => {
  const [filterValue, setFilterValue] = useState<string>('');
  const [filteredMessages, setFilteredMessages] = useState<SupportMessage[]>([]);
  const [message, setMessage] = useState<string>('');

  const endOfMessages: React.MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null);
  const currentUser: React.MutableRefObject<string> = useRef<string>(retrieveFromLocalStorage('user'));
  const hasUserScrolled: React.MutableRefObject<boolean> = useRef<boolean>(false);

  useEffect((): void => {
    if (messages) {
      setFilteredMessages(
        messages.filter((message: SupportMessage): boolean =>
          message.message.toLowerCase().includes(filterValue.toLowerCase())
        )
      );
    }
  }, [filterValue, messages]);

  useEffect((): void => {
    if (!hasUserScrolled.current) {
      endOfMessages.current?.scrollIntoView({ block: 'end', inline: 'nearest' });
    }
  }, [filteredMessages]);

  const handleScroll = (scrollEvent: UIEvent<HTMLDivElement>): void => {
    hasUserScrolled.current =
      scrollEvent.currentTarget.scrollHeight - scrollEvent.currentTarget.scrollTop !==
      scrollEvent.currentTarget.clientHeight;
  };

  const handleSendClick = (): void => {
    if (!message) {
      return;
    }

    createMessage(message, (): void => {
      setMessage('');
      endOfMessages.current?.scrollIntoView({ block: 'end', inline: 'nearest' });
    });
  };

  const renderMessages = (): ReactElement[] => {
    const dayMap: Map<string, SupportMessage[]> = new Map();

    const reversedMessages: SupportMessage[] = [...filteredMessages].reverse();

    reversedMessages.forEach((message: SupportMessage): void => {
      const { date } = message;
      const messages: SupportMessage[] | undefined = dayMap.get(date);
      if (!messages) {
        dayMap.set(date, [message]);
      } else {
        messages.push(message);
      }
    });

    const elements: ReactElement[] = [];

    dayMap.forEach((value: SupportMessage[], key: string): void => {
      elements.push(
        <div key={key}>
          <div className="support-chat__chat-box__date-bubble">
            <div>{value[0]?.date}</div>
          </div>
          {value.map((message: SupportMessage, index: number): ReactElement => {
            const right: boolean = message.userExternalReference === currentUser.current;

            return (
              <div className={getClassNames('support-chat__chat-box__bubble-container', { right })} key={index}>
                <div className={getClassNames('support-chat__chat-box__bubble', { right })}>
                  <p className={getClassNames('support-chat__chat-box__bubble__content', { right })}>
                    {message.message}
                  </p>
                  <p className={getClassNames('support-chat__chat-box__bubble__time', { right })}>{message.time}</p>
                </div>
              </div>
            );
          })}
        </div>
      );
    });

    return elements;
  };

  return (
    <ProgrammeLayout>
      <div className="support-chat" data-testid={`support-chat-${status}`}>
        <div className="support-chat__header">
          <div className="support-chat__header__icon-container">
            <ArrowIcon onClick={navigateBack} data-testid="arrow-icon" />
          </div>
          <h1>{formatUsername(messageOwner)}</h1>
        </div>
        <div className="support-chat__filter">
          <SearchIcon />
          <input
            value={filterValue}
            onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setFilterValue(target.value)}
            data-testid="chat-filter"
          />
        </div>
        <div className="support-chat__chat-box" onScroll={handleScroll}>
          {filteredMessages?.length > 0 && renderMessages()}
          {filteredMessages?.length === 0 && <span className="support-chat__no-messages">No Messages</span>}
          <div ref={endOfMessages} />
        </div>
        <div className={getClassNames('support-chat__chat-input', { disabled: status === ARCHIVED })}>
          <input
            value={message}
            onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setMessage(target.value)}
            disabled={status === ARCHIVED}
            data-testid="chat-input"
          />
          <SendIcon onClick={handleSendClick} data-testid="send-icon" />
        </div>
      </div>
    </ProgrammeLayout>
  );
};

export default SupportChat;
